import { FC, useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router";

import { Interactions } from "@vviinn/user-managment-client-ts";

import {
  useAppDispatch,
  useAppSelector,
  fetchCompanyOverviewData,
  fetchCompanySiteOverviewData,
} from "../store";

import { useApi } from "../api";

import { Restricted } from "../components/Restricted";

import { TimePeriodSelector } from "../dashboard/DateTime/TimePeriodSelector";
import { DetailedWrapper } from "../ui/layout/general/DetailedWrapper/DetailedWrapper";
import { CompanyAndSiteIds } from "./types";
import { SiteOverview } from "../dashboard/SiteOverview";
import { availableSections } from "../dashboard/constants";
import { CompanyOverview } from "../dashboard/CompanyOverview";
import { SubSectionWithStyles } from "../ui/layout";

const TIME_TILL_DASHBOARD_REFRESH = 1000 * 60 * 4;

const isDateValid = (date: number | null): boolean => {
  if (date === null) return false;
  const now = new Date();
  const updatedAt = new Date(date);
  const diff = now.getTime() - updatedAt.getTime();
  return diff < TIME_TILL_DASHBOARD_REFRESH;
};

export const Dashboard: FC = () => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();
  const [isSiteOverview, setIsSiteOverview] = useState(false);

  const { getApi } = useApi();
  const dispatch = useAppDispatch();

  const sites = useAppSelector((state) => state.sites.sites);
  const siteDomain = useAppSelector((state) => state.site?.domain);
  const selectedTimeRange = useAppSelector(
    (state) => state.dashboard.selectedTimeRange,
  );
  const loadedCompanyId = useAppSelector(
    (state) => state.dashboard.companyOverview.companyId,
  );
  const loadedCompanyTimeRange = useAppSelector(
    (state) => state.dashboard.companyOverview.timeRange,
  );
  const companyUpdatedAt = useAppSelector(
    (state) => state.dashboard.companyOverview.updatedAt,
  );
  const loadedSiteCompanyId = useAppSelector(
    (state) => state.dashboard.companySiteOverview.companyId,
  );
  const loadedSiteSiteId = useAppSelector(
    (state) => state.dashboard.companySiteOverview.siteId,
  );
  const loadedSiteTimeRange = useAppSelector(
    (state) => state.dashboard.companySiteOverview.timeRange,
  );
  const siteUpdatedAt = useAppSelector(
    (state) => state.dashboard.companySiteOverview.updatedAt,
  );

  const isSameCompanyData =
    companyId === loadedCompanyId &&
    selectedTimeRange === loadedCompanyTimeRange;

  const isSameSiteData =
    companyId === loadedSiteCompanyId &&
    siteId === loadedSiteSiteId &&
    selectedTimeRange === loadedSiteTimeRange;

  const fetchCompanyOverview = useCallback(async () => {
    if (
      isSameCompanyData &&
      isDateValid(companyUpdatedAt) &&
      isDateValid(siteUpdatedAt)
    )
      return;

    const params = [companyId].filter((x) => x);
    if (params.length === 0) return;
    const value = {
      companyId: companyId,
      timeRange: selectedTimeRange,
    };
    const api = await getApi();
    dispatch(fetchCompanyOverviewData({ api, value }));
  }, [companyId, selectedTimeRange]);

  useEffect(() => {
    fetchCompanyOverview();
  }, [fetchCompanyOverview]);

  const fetchSiteOverview = useCallback(async () => {
    if (
      isSameSiteData &&
      isDateValid(siteUpdatedAt) &&
      isDateValid(companyUpdatedAt)
    )
      return;

    const haveSite = sites.map((s) => s.id).includes(siteId);
    if (!haveSite) return;
    const params = [companyId, siteId].filter((x) => x);
    if (params.length === 0 || !companyId) return;
    const value = {
      companyId,
      siteId: siteId,
      timeRange: selectedTimeRange,
    };
    const api = await getApi();
    dispatch(fetchCompanySiteOverviewData({ api, value }));
  }, [siteId, selectedTimeRange]);

  useEffect(() => {
    fetchSiteOverview();
  }, [fetchSiteOverview]);

  return (
    <DetailedWrapper detailedPage="dashboard" detailedPageType="detail">
      <TimePeriodSelector />

      <CompanyOverview />

      {isSiteOverview && (
        <SubSectionWithStyles
          title="Site Overview"
          isSubHeader={false}
          withGroup={false}
        >
          <a
            href={`http://${siteDomain}`}
            target="_blank"
            rel="noreferrer"
            className="-mt-4 inline-block max-w-fit text-sm font-medium text-black underline hover:text-stone-900 dark:text-white hover:dark:text-stone-100"
          >
            {siteDomain}
          </a>
        </SubSectionWithStyles>
      )}

      <Restricted permissions="dashboard:services:view">
        {availableSections.map((section) => (
          <SiteOverview
            key={section}
            setIsSiteOverview={setIsSiteOverview}
            service={section as keyof Interactions}
          />
        ))}
      </Restricted>
    </DetailedWrapper>
  );
};
